import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { salesChannelService as defaultService } from '@services/admin/salesChannelService';

/**
 * SalesChannelSelect
 *
 * 一個封裝了載入邏輯的 SalesChannel 下拉選單。
 *
 * 特性：
 * 1. Lazy Load: 點擊選單時才觸發 API 載入。
 * 2. Preload: 如果有初始 value，會自動載入以確保 Label 顯示正確。
 * 3. Searchable: 支援後端關鍵字過濾。
 *
 * Props:
 * @param {Object} service - API Service (選填，預設為 admin service)
 * @param {number|string} merchantId - 貨主 ID，若有傳入則篩選該貨主
 * @param {boolean} fetchOnMount - 是否在掛載時立即載入 (預設 false)
 * @param {string|string[]} value - Form 綁定的值
 * @param {Function} onChange - Form 綁定的 onChange
 * @param {string} mode - 'multiple' | 'tags' | undefined
 */
export default function SalesChannelSelect({
  service = defaultService,
  merchantId,
  fetchOnMount = false,
  value,
  onChange,
  mode,
  ...props
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(false);

  // 載入資料的核心函數
  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        merchant_id: merchantId,
        page_size: 100, // 假設 100 夠用，若不夠需實作分頁或 Infinite Scroll
        ...params
      };

      // 支援 fetchList (Operator) 和 list (Admin) 兩種命名習慣
      const apiCall = service.fetchList || service.list;
      const response = await apiCall(queryParams);
      const data = response.data || [];

      // 轉換為 Select Option 格式
      const newOptions = data.map(item => ({
        label: item.name,
        value: item.id,
        raw: item
      }));

      setOptions(prev => {
        // 如果是搜尋模式（有帶參數），則替換選項（但保留已選的）
        // 如果是初始化或 lazy load，則合併
        const isSearch = !!params.search;

        if (isSearch) {
          return newOptions;
        }

        // 合併新舊選項，避免重複
        const map = new Map();
        prev.forEach(opt => map.set(opt.value, opt));
        newOptions.forEach(opt => map.set(opt.value, opt));
        return Array.from(map.values());
      });

      if (!params.search) setLoaded(true);
    } catch (error) {
      console.error(error);
      // 避免在 component unmount 後顯示錯誤
      message.error(t('common.error.fetch_failed', 'Failed to load merchant channels'));
    } finally {
      setLoading(false);
    }
  }, [service, merchantId, t]);

  // 1. 處理點擊載入 (Lazy Load)
  const handleDropdownVisibleChange = (open) => {
    if (open && !loaded && !loading) {
      fetchData();
    }
    props.onDropdownVisibleChange?.(open);
  };

  // 2. 處理後端搜尋 (Debounce)
  const debouncedSearch = useMemo(() => {
    const loadOptions = (value) => {
      if (value) {
        fetchData({ search: value });
      } else {
        // 如果搜尋為空，重新載入預設列表（如果之前是搜尋結果）
        fetchData();
      }
    };
    return debounce(loadOptions, 800);
  }, [fetchData]);

  const handleSearch = (val) => {
    debouncedSearch(val);
  };

  // 3. 處理初始值回填 (Preload)
  useEffect(() => {
    if (fetchOnMount) {
      fetchData();
      return;
    }

    const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
    if (hasValue && !loaded && !loading && !mountedRef.current) {
      fetchData();
      mountedRef.current = true; // 避免重複觸發
    }
  }, [value, loaded, loading, fetchOnMount, fetchData]);

  // 當 merchantId 改變時重置
  useEffect(() => {
    if (merchantId) {
      setOptions([]);
      setLoaded(false);
      mountedRef.current = false;
    }
  }, [merchantId]);

  return (
    <Select
      mode={mode}
      loading={loading}
      value={value}
      onChange={onChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      placeholder={t('common.placeholder.select', 'Select')}
      showSearch
      onSearch={handleSearch}
      filterOption={false} // 使用後端搜尋，關閉前端過濾
      options={options}
      allowClear
      {...props}
    />
  );
}
